import { create } from "zustand";
import { PERMISSIONS, ROLE_KEYS } from "../constants/permissions";
import {
	getAccessProfile,
	getRolePermissionsList,
	getUserRole,
} from "../services/api-server/roles_permissions";
import { useUserStore } from "./userStore";

interface ProjectRole {
	role: string;
	project_code: string;
	project_name: string;
}

interface RoleDefinition {
	key: string;
	name: string;
	scope?: "tenant" | "project";
	permissions: string[];
}

export type AccessKind =
	"super_admin" | "tenant" | "standard" | "examiner" | "none";

/** Server-resolved access profile for the current tenant (`GET /role-permissions/me`). */
export interface AccessProfile {
	kind: AccessKind;
	oid: string | null;
	email: string | null;
	/** Wells projects the user is assigned to as an external well examiner. */
	examinerProjectCodes: string[];
	external: boolean;
	/** The server applies the examiner allowlist to this session. */
	restricted: boolean;
}

interface PermissionState {
	// Data
	roleDefinitions: RoleDefinition[];
	tenantRoles: string[];
	projectRoles: ProjectRole[];
	accessProfile: AccessProfile | null;

	// Aggregated permissions (computed)
	tenantPermissions: Set<string>;
	projectPermissionsMap: Map<string, Set<string>>;

	// Loading state
	isLoading: boolean;
	error: string | null;
	isInitialized: boolean;

	// Actions
	initializePermissions: (tenantKey: string, tenantData: any) => Promise<void>;
	clearPermissions: () => void;
	hasPermission: (permission: string, projectCode?: string) => boolean;
	hasAnyPermission: (permissions: string[], projectCode?: string) => boolean;
	checkSuperAdmin: () => boolean;
	/** True when the session's only access to the tenant is as a well examiner. */
	isExaminerOnly: () => boolean;
	/** True when the user is assigned as well examiner on the project. */
	isWellExaminer: (projectCode: string) => boolean;
	/** True when the user has team-level (non-examiner) access to the project. */
	hasTeamAccess: (projectCode: string) => boolean;
}

// Helper functions
function determineTenantRoles(userEmail: string, tenantData: any): string[] {
	const roles: string[] = [];
	const email = userEmail.toLowerCase();

	if (
		tenantData?.tenant_admin?.some((e: string) => e.toLowerCase() === email)
	) {
		roles.push("tenant_admin");
	}

	if (
		tenantData?.tenant_members?.some((e: string) => e.toLowerCase() === email)
	) {
		roles.push("tenant_member");
	}

	return roles;
}

function parseProjectRoles(data: any): ProjectRole[] {
	if (!data) return [];

	// Handle both single object and array responses
	const rolesArray = Array.isArray(data) ? data : [data];

	return rolesArray
		.filter((r) => r && r.role && r.role !== "guest")
		.map((r) => ({
			role: r.role,
			project_code: r.project_code,
			project_name: r.project_name,
		}));
}

function getRoleScope(roleDef: RoleDefinition): "tenant" | "project" {
	if (roleDef.scope) {
		return roleDef.scope;
	}
	// Backward compatibility: infer scope from known keys
	if (roleDef.key === "tenant_admin" || roleDef.key === "tenant_member") {
		return "tenant";
	}
	return "project";
}

function aggregatePermissions(
	roleKeys: string[],
	roleDefinitions: RoleDefinition[],
	scopeFilter?: "tenant" | "project"
): Set<string> {
	const permissions = new Set<string>();

	roleKeys.forEach((roleKey) => {
		let roleDef = roleDefinitions.find((r) => r.key === roleKey);
		// Fallback to team_member for unknown roles from role_assignments
		if (!roleDef) {
			roleDef = roleDefinitions.find((r) => r.key === "team_member");
		}
		if (roleDef?.permissions) {
			// If scopeFilter is provided, only include roles matching that scope
			if (!scopeFilter || getRoleScope(roleDef) === scopeFilter) {
				roleDef.permissions.forEach((p) => permissions.add(p));
			}
		}
	});

	return permissions;
}

/** The access profile is additive; a failed lookup must not deny existing users. */
function fetchAccessProfile(tenantKey: string): Promise<AccessProfile | null> {
	return (getAccessProfile(tenantKey) as Promise<AccessProfile | null>).catch(
		(error) => {
			console.warn("[PermissionStore] Failed to load access profile:", error);
			return null;
		}
	);
}

function buildProjectPermissionsMap(
	projectRoles: ProjectRole[],
	tenantRoles: string[],
	roleDefinitions: RoleDefinition[],
	accessProfile: AccessProfile | null
): Map<string, Set<string>> {
	const projectPermissionsMap = new Map<string, Set<string>>();

	// Only project-scoped roles, with tenant roles as effective roles. A user
	// can hold several roles on one project, so permissions are merged.
	projectRoles.forEach((projRole) => {
		const permissions = aggregatePermissions(
			[...tenantRoles, projRole.role],
			roleDefinitions,
			"project"
		);
		const existing = projectPermissionsMap.get(projRole.project_code);
		if (existing) {
			permissions.forEach((p) => existing.add(p));
		} else {
			projectPermissionsMap.set(projRole.project_code, permissions);
		}
	});

	// Examiner assignments grant project visibility regardless of how the
	// tenant's role definitions are configured.
	(accessProfile?.examinerProjectCodes || []).forEach((projectCode) => {
		const permissions = projectPermissionsMap.get(projectCode) ?? new Set();
		permissions.add(PERMISSIONS.PROJECT.VIEW);
		projectPermissionsMap.set(projectCode, permissions);
	});

	return projectPermissionsMap;
}

export const usePermissionStore = create<PermissionState>((set, get) => ({
	roleDefinitions: [],
	tenantRoles: [],
	projectRoles: [],
	accessProfile: null,
	tenantPermissions: new Set(),
	projectPermissionsMap: new Map(),
	isLoading: false,
	error: null,
	isInitialized: false,

	initializePermissions: async (tenantKey: string, tenantData: any) => {
		const userStore = useUserStore.getState();
		const user = userStore.getCurrentUser();
		const userRoles = userStore.getUserRole();

		if (!user?.email) {
			console.warn(
				"[PermissionStore] No user email found, skipping initialization"
			);
			return;
		}

		const superAdminTag = import.meta.env.VITE_SUPER_ADMIN_TAG;
		console.log(
			"[PermissionStore] Initializing permissions for tenant:",
			tenantKey
		);

		set({ isLoading: true, error: null });

		try {
			// Check if super admin
			if (userRoles?.includes(superAdminTag)) {
				console.log(
					"[PermissionStore] User is super admin, granting all permissions"
				);
				const accessProfile = await fetchAccessProfile(tenantKey);
				set({
					roleDefinitions: [],
					tenantRoles: ["super_admin"],
					projectRoles: [],
					accessProfile,
					tenantPermissions: new Set(["*"]), // All permissions
					projectPermissionsMap: new Map(),
					isLoading: false,
					isInitialized: true,
				});
				return;
			}

			// Fetch data in parallel
			const [roleDefinitions, projectRolesData, accessProfile] =
				await Promise.all([
					getRolePermissionsList(tenantKey),
					getUserRole(tenantKey, user.email),
					fetchAccessProfile(tenantKey),
				]);

			// Determine tenant roles from tenantData
			const tenantRoles = determineTenantRoles(user.email, tenantData);

			// Parse project roles
			const projectRoles = parseProjectRoles(projectRolesData);

			// Aggregate tenant permissions (only from tenant-scoped roles)
			const tenantPermissions = aggregatePermissions(
				tenantRoles,
				roleDefinitions as RoleDefinition[],
				"tenant"
			);

			const projectPermissionsMap = buildProjectPermissionsMap(
				projectRoles,
				tenantRoles,
				roleDefinitions as RoleDefinition[],
				accessProfile
			);

			set({
				roleDefinitions: roleDefinitions as RoleDefinition[],
				tenantRoles,
				projectRoles,
				accessProfile,
				tenantPermissions,
				projectPermissionsMap,
				isLoading: false,
				isInitialized: true,
			});
			console.log("[PermissionStore] Permissions initialized successfully");
		} catch (error) {
			console.error(
				"[PermissionStore] Failed to initialize permissions:",
				error
			);
			set({
				error: "Failed to load permissions",
				isLoading: false,
				isInitialized: true,
				accessProfile: null,
				tenantPermissions: new Set(), // Deny access on error
				projectPermissionsMap: new Map(),
			});
		}
	},

	clearPermissions: () => {
		set({
			roleDefinitions: [],
			tenantRoles: [],
			projectRoles: [],
			accessProfile: null,
			tenantPermissions: new Set(),
			projectPermissionsMap: new Map(),
			isLoading: false,
			error: null,
			isInitialized: false,
		});
	},

	hasPermission: (permission: string, projectCode?: string) => {
		const { tenantPermissions, projectPermissionsMap, isInitialized } = get();

		// If not initialized yet, deny access (don't block during loading)
		if (!isInitialized) {
			return false;
		}

		// Check tenant-level permissions first
		if (tenantPermissions.has(permission) || tenantPermissions.has("*")) {
			return true;
		}

		// Check project-specific permissions if projectCode provided
		if (projectCode) {
			const projectPerms = projectPermissionsMap.get(projectCode);
			if (projectPerms?.has(permission) || projectPerms?.has("*")) {
				return true;
			}
			return false;
		}

		// If no projectCode provided, check if user has this permission in ANY project
		// This allows project-scoped roles (e.g., project_manager) to access global pages like /projects
		for (const perms of projectPermissionsMap.values()) {
			if (perms.has(permission) || perms.has("*")) {
				return true;
			}
		}

		return false;
	},

	hasAnyPermission: (permissions: string[], projectCode?: string) => {
		return permissions.some((p) => get().hasPermission(p, projectCode));
	},

	checkSuperAdmin: () => {
		const userStore = useUserStore.getState();
		const userRoles = userStore.getUserRole();
		const superAdminTag = import.meta.env.VITE_SUPER_ADMIN_TAG;
		return userRoles?.includes(superAdminTag) || false;
	},

	isExaminerOnly: () => !!get().accessProfile?.restricted,

	isWellExaminer: (projectCode: string) =>
		!!projectCode &&
		(get().accessProfile?.examinerProjectCodes || []).includes(projectCode),

	hasTeamAccess: (projectCode: string) => {
		const { accessProfile, projectRoles } = get();
		if (!accessProfile) return false;
		if (
			accessProfile.kind === "super_admin" ||
			accessProfile.kind === "tenant"
		) {
			return true;
		}
		if (accessProfile.kind !== "standard") return false;
		return projectRoles.some(
			(role) =>
				role.project_code === projectCode &&
				role.role !== ROLE_KEYS.WELL_EXAMINER
		);
	},
}));

// Export non-hook accessor for use outside components
export const getPermissionStore = () => usePermissionStore.getState();
